import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Button from '../UI/Button';

const VillageContainer = styled.section`
  padding: 6rem 0;
  background: ${({ theme }) => theme.colors.background.secondary};
  position: relative;
  overflow: hidden;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 3rem 0;
  }
`;

const AnimatedBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 15% 25%, rgba(155, 89, 182, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 85% 75%, rgba(0, 128, 214, 0.08) 0%, transparent 50%),
      linear-gradient(135deg, rgba(255, 229, 0, 0.02) 0%, transparent 70%);
  }
`;

const VillageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 2rem;
    padding: 0 1rem;
  }
`;

const VillageTitle = styled(motion.h2)`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  background: ${({ theme }) => theme.gradients.cyber};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
  position: relative;
  
  &::after {
    content: '🏴‍☠️';
    position: absolute;
    top: -10px;
    right: -20px;
    font-size: 2rem;
    animation: bounce 2s infinite;
  }
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-10px); }
    60% { transform: translateY(-5px); }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.5rem;
    
    &::after {
      font-size: 1.5rem;
      top: -5px;
      right: -15px;
    }
  }
`;

const VillageSubtitle = styled(motion.p)`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto 2rem;
  line-height: 1.6;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
    padding: 0 0.5rem;
  }
`;

const VillageDescription = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 20px;
  padding: 2.5rem;
  margin: 0 auto 4rem;
  max-width: 900px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 2;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${({ theme }) => theme.gradients.cyber};
    border-radius: 20px 20px 0 0;
  }
  
  p {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 1.1rem;
    line-height: 1.7;
    margin-bottom: 1.5rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1.5rem;
    margin: 0 1rem 2rem 1rem;
    
    p {
      font-size: 1rem;
      margin-bottom: 1rem;
    }
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 0 1rem;
    margin-bottom: 2rem;
  }
`;

const SkillCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 20px;
  padding: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease;
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(155, 89, 182, 0.05) 0%, rgba(0, 128, 214, 0.05) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: ${({ theme }) => theme.gradients.cyber};
    transform: scaleX(0);
    transition: transform 0.3s ease;
    transform-origin: left;
  }
  
  &:hover {
    transform: translateY(-8px);
    border-color: ${({ theme }) => theme.colors.primary.cyan};
    box-shadow: 0 20px 40px rgba(155, 89, 182, 0.15);
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  &:hover::after {
    transform: scaleX(1);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1.5rem;
    
    &:hover {
      transform: none;
      box-shadow: 0 10px 25px rgba(155, 89, 182, 0.1);
    }
  }
`;

const SkillIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  .emoji {
    font-size: 3rem;
  }
  
  .tool-name {
    font-size: 1.2rem;
    color: ${({ theme }) => theme.colors.primary.yellow};
    font-weight: 700;
    font-family: ${({ theme }) => theme.fonts.mono};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    
    .emoji {
      font-size: 2.5rem;
    }
    
    .tool-name {
      font-size: 1rem;
    }
  }
`;

const SkillTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary.cyan};
  font-size: 1.4rem;
  margin-bottom: 1rem;
  font-weight: 600;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.2rem;
    margin-bottom: 0.75rem;
  }
`;

const SkillDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.9rem;
  }
`;

const SkillDuration = styled.div`
  background: rgba(0, 128, 214, 0.1);
  color: ${({ theme }) => theme.colors.primary.blue};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  border: 1px solid rgba(0, 128, 214, 0.2);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0.4rem 0.8rem;
    font-size: 0.75rem;
  }
`;

const RequirementsSection = styled.div`
  background: ${({ theme }) => theme.gradients.card};
  border-radius: 25px;
  padding: 3rem;
  margin: 4rem auto;
  max-width: 1000px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  backdrop-filter: blur(20px);
  position: relative;
  z-index: 2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 2rem 1.5rem;
    margin: 2rem 1rem;
    border-radius: 20px;
  }
`;

const RequirementsTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary.yellow};
  font-size: 2rem;
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 700;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const RequirementsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const RequirementItem = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 15px;
  padding: 1.5rem;
  border-left: 4px solid ${({ theme }) => theme.colors.primary.cyan};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateX(5px);
    background: rgba(0, 212, 255, 0.05);
  }
  
  .icon {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }
  
  h4 {
    color: ${({ theme }) => theme.colors.primary.cyan};
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    font-weight: 600;
  }
  
  p {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 0.9rem;
    line-height: 1.5;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1rem;
    
    &:hover {
      transform: none;
    }
    
    .icon {
      font-size: 1.3rem;
    }
    
    h4 {
      font-size: 1rem;
    }
    
    p {
      font-size: 0.85rem;
    }
  }
`;

const ContactSection = styled.div`
  text-align: center;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 20px;
  padding: 3rem;
  margin: 4rem auto 0;
  max-width: 600px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;
  z-index: 2;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: ${({ theme }) => theme.gradients.primary};
    border-radius: 20px 20px 0 0;
  }
  
  h3 {
    color: ${({ theme }) => theme.colors.primary.yellow};
    font-size: 1.8rem;
    margin-bottom: 1rem;
    font-weight: 700;
  }
  
  p {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 2rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 2rem 1.5rem;
    margin: 2rem 1rem 0 1rem;
    
    h3 {
      font-size: 1.5rem;
    }
    
    p {
      font-size: 0.9rem;
      margin-bottom: 1.5rem;
    }
  }
`;

const HackingVillage = () => {
  const skills = [
    {
      icon: '🔍',
      tool: 'Nmap',
      title: 'Network Enumeration',
      description: 'Learn to discover and map network services, identify open ports, and gather intelligence about target systems using the industry-standard Nmap scanner.',
      duration: '30-45 minutes'
    },
    {
      icon: '🌐',
      tool: 'Burp Suite',
      title: 'Web Application Assessment',
      description: 'Master web application security testing with Burp Suite, including intercepting requests, analyzing responses, and identifying vulnerabilities.',
      duration: '30-45 minutes'
    },
    {
      icon: '💥',
      tool: 'Metasploit',
      title: 'Exploitation Framework',
      description: 'Dive into the world of exploitation using Metasploit to understand how vulnerabilities are exploited and systems are compromised.',
      duration: '30-45 minutes'
    },
    {
      icon: '🔐',
      tool: 'Hashcat',
      title: 'Password Cracking',
      description: 'Explore password security by learning various cracking techniques and understanding how weak passwords can be compromised.',
      duration: '30-45 minutes'
    },
    {
      icon: '🛡️',
      tool: 'Tenable Nessus',
      title: 'Vulnerability Assessment',
      description: 'Conduct comprehensive vulnerability assessments to identify security weaknesses and prioritize remediation efforts.',
      duration: '30-45 minutes'
    }
  ];

  const requirements = [
    {
      icon: '💻',
      title: 'Bring Your Laptop',
      description: 'All you need is a laptop with Remote Desktop Protocol (RDP) client to connect to our Kali VMs.'
    },
    {
      icon: '🔌',
      title: 'Network Connection',
      description: 'Wired connection recommended for best performance, but wireless is available too.'
    },
    {
      icon: '🏃‍♂️',
      title: 'Your Own Pace',
      description: 'Complete stations in any order, come and go as you wish, with lab assistants ready to help.'
    },
    {
      icon: '🛠️',
      title: 'Custom Setup Welcome',
      description: 'Have your own pentesting setup? Feel free to bring it and jump right in!'
    }
  ];

  const handleContactLabs = () => {
    const subject = encodeURIComponent('Security BSides Albuquerque 2025 - Cyber Skills Labs Inquiry');
    const body = encodeURIComponent(`Hello Cyber Skills Labs Team,

I'm interested in participating in the Cyber Skills Labs at Security BSides Albuquerque 2025.

I have some questions about:
- [ ] Lab requirements and setup
- [ ] Specific tools and exercises
- [ ] Difficulty level and prerequisites
- [ ] Equipment recommendations
- [ ] Other: _______________

Additional details:
Experience Level: [Beginner/Intermediate/Advanced]
Specific Areas of Interest: _______________

Thank you for offering these hands-on learning opportunities!

Best regards,
[Your Name]`);

    const emailAddress = 'cyberskillslabs@gmail.com';
    const mailtoLink = `mailto:${emailAddress}?subject=${subject}&body=${body}`;
    
    window.location.href = mailtoLink;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <VillageContainer id="hackingvillage">
      <AnimatedBackground />
      <div className="container">
        <VillageHeader>
          <VillageTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Hacking Village
          </VillageTitle>
          <VillageSubtitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Cyber Skills Labs - Learn Pentesting Fundamentals Through Hands-On Experience
          </VillageSubtitle>
        </VillageHeader>

        <VillageDescription
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <p>
            Do you want to be a pentester, but don't know where to start? Do you have an interest in pentesting, 
            but need some help getting the fundamentals down? Are you curious about the tools pentesters use to 
            break into things? Are you a defender or an IT or cyber security manager, and want to learn or understand 
            the fundamental security tools better?
          </p>
          <p>
            <strong>If any of this resonates then the Cyber Skills Labs are for you!</strong> The Labs are a series of 
            five stations, each one with a set of activities and tutorials focused on a pentesting fundamental. 
            Each station is designed to take 30-45 minutes to complete with web-based guides and walkthroughs.
          </p>
        </VillageDescription>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <SkillsGrid>
            {skills.map((skill, index) => (
              <SkillCard
                key={skill.title}
                variants={cardVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SkillIcon>
                  <span className="emoji">{skill.icon}</span>
                  <span className="tool-name">{skill.tool}</span>
                </SkillIcon>
                <SkillTitle>{skill.title}</SkillTitle>
                <SkillDescription>{skill.description}</SkillDescription>
                <SkillDuration>⏱️ {skill.duration}</SkillDuration>
              </SkillCard>
            ))}
          </SkillsGrid>
        </motion.div>

        <RequirementsSection>
          <RequirementsTitle>🎯 What You Need to Participate</RequirementsTitle>
          <RequirementsList>
            {requirements.map((req, index) => (
              <RequirementItem
                key={req.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="icon">{req.icon}</div>
                <h4>{req.title}</h4>
                <p>{req.description}</p>
              </RequirementItem>
            ))}
          </RequirementsList>
        </RequirementsSection>

        <ContactSection>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3>🚀 Ready to Get Started?</h3>
            <p>
              Join us at Cyber Skills Labs for a fun and low-pressure way to get your feet wet in pentesting! 
              Lab Assistants will be available to help if you get stuck.
            </p>
            <Button 
              variant="primary" 
              size="large"
              onClick={handleContactLabs}
            >
              📧 Contact Cyber Skills Labs
            </Button>
          </motion.div>
        </ContactSection>
      </div>
    </VillageContainer>
  );
};

export default HackingVillage;